import type { FC } from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { ContentControls } from "./ContentControls";
import { ContentDescription } from "./ContentDescription";
import { useDateFormat, usePlatform, useTranslation } from "@/shared/hooks";
import type { ContentQueryType } from "@/types/graphql/site.generated";

type Content = NonNullable<ContentQueryType["content"]>;

interface ContentBodyProps {
  content: Content;
  /** Передаётся StreamScreen, пока стрим идёт (status=STARTED) — заменяет
   * строку с датой публикации на LIVE-бейдж + счётчик зрителей. */
  live?: { viewersOnline: number };
}

// Порт референсного arena/components/ContentBody: заголовок → строка метаданных
// (дата трансляции у эфира, публикации у видео или, в live-режиме, LIVE-бейдж и счётчик зрителей — рядом
// с ней ряд outlined-кнопок контролов) → теги-пилюли → разворачиваемое
// описание.
export const ContentBody: FC<ContentBodyProps> = ({ content, live }) => {
  const { platform } = usePlatform();
  const { t } = useTranslation();
  const { formatDate } = useDateFormat();

  // У эфира показываем дату трансляции, а не публикации: анонс боя
  // 10 октября, опубликованный 2 октября, показывал «2 października» под
  // заголовком (tvcom.ottkit.io, 2026-10-10). У видео дата публикации.
  const eventDate = content.stream?.startAt ?? content.publishAt;

  const isLikesEnabled = platform?.isLikesEnabled !== false;
  const isFavoritesEnabled = platform?.isFavoritesEnabled !== false;

  return (
    <Box
      sx={{
        pt: { xs: 1.5, sm: 3 },
        pb: { xs: 0, sm: 3 },
        px: { xs: 1.5, sm: 3 },
      }}
    >
      <Stack spacing={{ xs: 1.5, sm: 2 }}>
        <Typography
          variant="h3"
          component="h1"
          sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem", lg: "1.75rem" } }}
        >
          {content.title}
        </Typography>

        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={{ xs: 1.5, md: 2 }}
          sx={{
            pb: { xs: 1.5, sm: 2.5 },
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ color: "text.muted", flex: 1, minWidth: 0 }}
          >
            {live ? (
              <>
                <Chip variant="badge" label={t("stream.live.badge")} />
                <Typography variant="overline">
                  {t("stream.live.viewersOnline", {
                    count: live.viewersOnline,
                  })}
                </Typography>
              </>
            ) : (
              eventDate && (
                <Typography variant="overline">
                  {formatDate(eventDate)}
                </Typography>
              )
            )}
          </Stack>
          <ContentControls
            content={content}
            isLikesEnabled={isLikesEnabled}
            isFavoritesEnabled={isFavoritesEnabled}
          />
        </Stack>

        {content.tags.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {content.tags.map((tag) => (
              <Chip
                key={tag.id}
                label={tag.title}
                component="a"
                href={`/tag/${tag.id}`}
                clickable
              />
            ))}
          </Stack>
        )}

        {content.description && (
          <ContentDescription>{content.description}</ContentDescription>
        )}
      </Stack>
    </Box>
  );
};
